// <cards-slider> — enhancement layer for the `siclips-product-cards` section.
// The cards are fully server-rendered inside a native horizontal scroll track (flex + overflow-x),
// so with no JS the section still works (native swipe/scroll, no dots/arrows). This element only
// ENHANCES: builds one dot per card, tracks the active card while scrolling (nearest-center,
// rect-based so it is RTL-safe), makes dots/arrows scroll the track, auto-hides dots+arrows when
// everything already fits (data-pcs-static), and follows theme-editor block selection.
//
// Editor-lifecycle pattern (CLAUDE.md §6.11): the editor MOVES the element on drag-reorder, so
// element-level listeners bind once per element (_boundEl, never reset) while document listeners
// re-bind per connect (_docBound, removed on disconnect).

class CardsSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-pcs-track]');
    this.dotsWrap = this.querySelector('[data-pcs-dots]');
    this.prevBtn = this.querySelector('[data-pcs-prev]');
    this.nextBtn = this.querySelector('[data-pcs-next]');
    if (!this.track) return;

    this.rmq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.cards = Array.from(this.track.querySelectorAll('.pcs-card'));
    this.index = 0;

    this._buildDots();

    if (!this._boundEl) {
      this._boundEl = true;
      this.track.addEventListener('scroll', () => this._queueSync(), { passive: true });
      // aria-disabled (not the disabled attr): disabling a focused button would drop
      // keyboard focus to <body> mid-navigation — the click handler no-ops instead
      const armed = (btn, dir) => btn.addEventListener('click', () => {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        this._step(dir);
      });
      if (this.prevBtn) armed(this.prevBtn, -1);
      if (this.nextBtn) armed(this.nextBtn, 1);
    }

    if (!this._docBound) {
      this._docBound = true;
      this._onBlockSelect = (e) => {
        const card = this.cards.find((c) => c === e.target || c.contains(e.target));
        if (card) this._scrollToCard(card);
      };
      document.addEventListener('shopify:block:select', this._onBlockSelect);
    }

    if ('ResizeObserver' in window && !this._ro) {
      this._ro = new ResizeObserver(() => this._queueSync());
      this._ro.observe(this.track);
    }

    // card add/remove changes scrollWidth WITHOUT resizing the track box (no RO event) —
    // a childList observer re-collects cards, rebuilds dots and re-evaluates static mode
    // (also covers any editor DOM patching that skips a full element reconnect)
    if ('MutationObserver' in window && !this._mo) {
      this._mo = new MutationObserver(() => {
        this.cards = Array.from(this.track.querySelectorAll('.pcs-card'));
        this._buildDots();
        this._sync();
      });
      this._mo.observe(this.track, { childList: true });
    }

    this._sync();
  }

  disconnectedCallback() {
    if (this._docBound) {
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      this._docBound = false;
    }
    if (this._ro) { this._ro.disconnect(); this._ro = null; }
    if (this._mo) { this._mo.disconnect(); this._mo = null; }
  }

  // ---------- dots ----------
  _buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.replaceChildren();
    this.dots = this.cards.map((card, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pcs__dot';
      b.setAttribute('aria-label', `מוצר ${i + 1}`);
      b.addEventListener('click', () => this._scrollToCard(this.cards[i]));
      this.dotsWrap.appendChild(b);
      return b;
    });
  }

  // ---------- state sync (rAF-throttled) ----------
  _queueSync() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._ticking = false;
      this._sync();
    });
  }

  _sync() {
    const track = this.track;
    if (!track || !this.cards.length) return;

    // static = everything fits, nothing to navigate -> CSS hides dots + arrows
    const scrollable = track.scrollWidth > track.clientWidth + 1;
    this.toggleAttribute('data-pcs-static', !scrollable);
    if (this.prevBtn) this.prevBtn.hidden = !scrollable;
    if (this.nextBtn) this.nextBtn.hidden = !scrollable;
    if (!scrollable) return;

    // nearest-center active card (rects are direction-agnostic -> RTL-safe)
    const tr = track.getBoundingClientRect();
    const center = tr.left + tr.width / 2;
    let best = 0;
    let bestDist = Infinity;
    this.cards.forEach((card, i) => {
      const r = card.getBoundingClientRect();
      const d = Math.abs(r.left + r.width / 2 - center);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    this.index = best;

    if (this.dots) {
      this.dots.forEach((b, i) => {
        const on = i === best;
        b.classList.toggle('is-active', on);
        if (on) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
    }

    // arrows: disabled at the extremes (epsilon absorbs sub-pixel scroll positions)
    const max = track.scrollWidth - track.clientWidth;
    const pos = Math.abs(track.scrollLeft); // RTL browsers report negative scrollLeft
    if (this.prevBtn) this.prevBtn.setAttribute('aria-disabled', String(pos <= 1));
    if (this.nextBtn) this.nextBtn.setAttribute('aria-disabled', String(pos >= max - 1));
  }

  // ---------- navigation ----------
  _behavior() {
    return this.rmq && this.rmq.matches ? 'auto' : 'smooth';
  }

  _scrollToCard(card) {
    if (!card || !this.track) return;
    const tr = this.track.getBoundingClientRect();
    const r = card.getBoundingClientRect();
    // align the card's center to the track's center; rect delta works in LTR and RTL alike
    const delta = r.left + r.width / 2 - (tr.left + tr.width / 2);
    this.track.scrollBy({ left: delta, behavior: this._behavior() });
  }

  _step(dir) {
    // dir: +1 = forward in reading order (next card in DOM), -1 = back
    const target = this.cards[Math.min(Math.max(this.index + dir, 0), this.cards.length - 1)];
    this._scrollToCard(target);
  }
}

if (!customElements.get('cards-slider')) {
  customElements.define('cards-slider', CardsSlider);
}
